import { useState, useEffect, useCallback, useRef } from 'react';
import { taskApi } from '../../../../tasks/api/taskApi';
import type { TaskDependency } from '../../../../tasks/types/task.types';

export interface DependencyGraph {
  /** taskId → IDs of tasks that depend on this task (downstream / blocked by this) */
  downstream: Map<string, string[]>;
  /** taskId → IDs of tasks this task depends on (upstream / blocks this) */
  upstream: Map<string, string[]>;
  /** taskId → raw dependency records */
  raw: Map<string, { dependencies: TaskDependency[]; dependedOnBy: TaskDependency[] }>;
}

const EMPTY_GRAPH: DependencyGraph = {
  downstream: new Map(),
  upstream: new Map(),
  raw: new Map(),
};

/**
 * Fetches the full engagement dependency graph in one call and builds
 * downstream/upstream maps for Gantt cascade logic + arrow rendering.
 */
export function useGanttDependencies(matterId: string) {
  const [graph, setGraph] = useState<DependencyGraph>(EMPTY_GRAPH);
  const [loading, setLoading] = useState(false);
  const cancelledRef = useRef(false);
  const matterIdRef = useRef('');

  const fetchGraph = useCallback(async (targetMatterId: string) => {
    if (!targetMatterId) {
      setGraph(EMPTY_GRAPH);
      return;
    }

    cancelledRef.current = false;
    setLoading(true);

    const downstream = new Map<string, string[]>();
    const upstream = new Map<string, string[]>();
    const raw = new Map<string, { dependencies: TaskDependency[]; dependedOnBy: TaskDependency[] }>();

    try {
      const response = await taskApi.getEngagementDependencies(targetMatterId) as any;
      const dependencies: TaskDependency[] = response.dependencies || response.data?.dependencies || [];

      for (const dep of dependencies) {
        if (!raw.has(dep.task_id)) {
          raw.set(dep.task_id, { dependencies: [], dependedOnBy: [] });
        }
        if (!raw.has(dep.depends_on_task_id)) {
          raw.set(dep.depends_on_task_id, { dependencies: [], dependedOnBy: [] });
        }

        raw.get(dep.task_id)!.dependencies.push(dep);
        raw.get(dep.depends_on_task_id)!.dependedOnBy.push(dep);

        if (dep.dependency_type !== 'blocks') continue;

        const downstreamList = downstream.get(dep.depends_on_task_id) || [];
        downstreamList.push(dep.task_id);
        downstream.set(dep.depends_on_task_id, downstreamList);

        const upstreamList = upstream.get(dep.task_id) || [];
        upstreamList.push(dep.depends_on_task_id);
        upstream.set(dep.task_id, upstreamList);
      }
    } catch {
      // Keep empty graph on fetch failure
    } finally {
      if (!cancelledRef.current) {
        setGraph({ downstream, upstream, raw });
        setLoading(false);
      }
    }
  }, []);

  // Stable identity check: only re-fetch when matter ID changes
  useEffect(() => {
    if (!matterId) {
      setGraph(EMPTY_GRAPH);
      matterIdRef.current = '';
      return;
    }
    if (matterId === matterIdRef.current) return;
    matterIdRef.current = matterId;

    fetchGraph(matterId);
    return () => { cancelledRef.current = true; };
  }, [matterId, fetchGraph]);

  const refresh = useCallback(() => {
    if (!matterId) {
      setGraph(EMPTY_GRAPH);
      return;
    }
    fetchGraph(matterId);
  }, [matterId, fetchGraph]);

  return { graph, loading, refresh };
}
